import { useEffect, useState, useCallback } from "react";
import { Bot, Plus, Pencil, Trash2, Check, X, ChevronDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import {
  adminListSupportFacts,
  adminCreateSupportFact,
  adminUpdateSupportFact,
  adminDeleteSupportFact,
} from "@/lib/api";

function FactRow({ fact, onSaved, onDeleted }) {
  const [editing, setEditing] = useState(false);
  const [q, setQ] = useState(fact.question);
  const [a, setA] = useState(fact.answer);
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (!q.trim() || !a.trim()) {
      toast.error("Frage und Antwort dürfen nicht leer sein.");
      return;
    }
    setBusy(true);
    try {
      const updated = await adminUpdateSupportFact(fact.id, { question: q.trim(), answer: a.trim() });
      onSaved(updated);
      setEditing(false);
      toast.success("Gespeichert.");
    } catch {
      toast.error("Speichern fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    setBusy(true);
    try {
      await adminDeleteSupportFact(fact.id);
      onDeleted(fact.id);
      toast.success("Eintrag gelöscht.");
    } catch {
      toast.error("Löschen fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  if (editing) {
    return (
      <div className="bg-white border border-border rounded-lg p-3 space-y-2" data-testid={`fact-edit-${fact.id}`}>
        <input
          value={q}
          onChange={(e) => setQ(e.target.value)}
          className="w-full rounded-md border border-border bg-white px-3 py-2 text-sm font-medium outline-none focus:border-primary"
          data-testid={`fact-edit-question-${fact.id}`}
        />
        <textarea
          value={a}
          onChange={(e) => setA(e.target.value)}
          rows={3}
          className="w-full rounded-md border border-border bg-white px-3 py-2 text-sm outline-none focus:border-primary"
          data-testid={`fact-edit-answer-${fact.id}`}
        />
        <div className="flex gap-2">
          <button
            onClick={save}
            disabled={busy}
            className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground disabled:opacity-50"
            data-testid={`fact-save-${fact.id}`}
          >
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Speichern
          </button>
          <button
            onClick={() => {
              setEditing(false);
              setQ(fact.question);
              setA(fact.answer);
            }}
            className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-sm"
          >
            <X className="h-4 w-4" /> Abbrechen
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-border rounded-lg p-3" data-testid={`fact-row-${fact.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="font-medium text-sm">{fact.question}</p>
          <p className="text-sm text-muted-foreground mt-1 whitespace-pre-line">{fact.answer}</p>
        </div>
        <div className="flex shrink-0 gap-1">
          <button
            onClick={() => setEditing(true)}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-muted hover:text-foreground"
            data-testid={`fact-edit-btn-${fact.id}`}
            aria-label="Bearbeiten"
          >
            <Pencil className="h-4 w-4" />
          </button>
          <button
            onClick={remove}
            disabled={busy}
            className="rounded-md p-1.5 text-muted-foreground hover:bg-red-50 hover:text-red-600"
            data-testid={`fact-delete-btn-${fact.id}`}
            aria-label="Löschen"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default function WalterFactsPanel({ defaultOpen = false }) {
  const [facts, setFacts] = useState([]);
  const [openPanel, setOpenPanel] = useState(defaultOpen);
  const [adding, setAdding] = useState(false);
  const [nq, setNq] = useState("");
  const [na, setNa] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      setFacts(await adminListSupportFacts());
    } catch {
      /* still */
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const add = async () => {
    if (!nq.trim() || !na.trim()) {
      toast.error("Frage und Antwort dürfen nicht leer sein.");
      return;
    }
    setBusy(true);
    try {
      const created = await adminCreateSupportFact({ question: nq.trim(), answer: na.trim() });
      setFacts((f) => [...f, created]);
      setNq("");
      setNa("");
      setAdding(false);
      toast.success("Eintrag hinzugefügt.");
    } catch {
      toast.error("Hinzufügen fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-border rounded-lg" data-testid="walter-facts-panel">
      <button
        onClick={() => setOpenPanel((v) => !v)}
        className="flex w-full items-center gap-2 px-4 py-3 text-left"
        data-testid="walter-facts-toggle"
      >
        <Bot className="h-5 w-5 text-primary" />
        <h2 className="text-lg font-semibold">WALTER – Support-Wissen</h2>
        <span className="text-xs bg-muted rounded-full px-2 py-0.5 text-muted-foreground">
          {facts.length} Einträge
        </span>
        <ChevronDown
          className={`ml-auto h-5 w-5 text-muted-foreground transition-transform ${openPanel ? "rotate-180" : ""}`}
        />
      </button>

      {openPanel && (
        <div className="border-t border-border p-4 space-y-3">
          <p className="text-sm text-muted-foreground">
            Diese FAQ- und Preis-Fakten nutzt WALTER zusätzlich zum Handbuch als Wissensbasis. Änderungen wirken
            innerhalb einer Minute.
          </p>

          {facts.map((f) => (
            <FactRow
              key={f.id}
              fact={f}
              onSaved={(u) => setFacts((prev) => prev.map((x) => (x.id === u.id ? u : x)))}
              onDeleted={(id) => setFacts((prev) => prev.filter((x) => x.id !== id))}
            />
          ))}

          {adding ? (
            <div className="bg-muted/40 border border-border rounded-lg p-3 space-y-2" data-testid="walter-fact-add-form">
              <input
                value={nq}
                onChange={(e) => setNq(e.target.value)}
                placeholder="Frage / Stichwort"
                className="w-full rounded-md border border-border bg-white px-3 py-2 text-sm font-medium outline-none focus:border-primary"
                data-testid="walter-fact-add-question"
              />
              <textarea
                value={na}
                onChange={(e) => setNa(e.target.value)}
                rows={3}
                placeholder="Antwort"
                className="w-full rounded-md border border-border bg-white px-3 py-2 text-sm outline-none focus:border-primary"
                data-testid="walter-fact-add-answer"
              />
              <div className="flex gap-2">
                <button
                  onClick={add}
                  disabled={busy}
                  className="inline-flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground disabled:opacity-50"
                  data-testid="walter-fact-add-save"
                >
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Hinzufügen
                </button>
                <button
                  onClick={() => {
                    setAdding(false);
                    setNq("");
                    setNa("");
                  }}
                  className="inline-flex items-center gap-1 rounded-md border border-border px-3 py-1.5 text-sm"
                >
                  <X className="h-4 w-4" /> Abbrechen
                </button>
              </div>
            </div>
          ) : (
            <button
              onClick={() => setAdding(true)}
              className="inline-flex items-center gap-1 rounded-md border border-dashed border-border px-3 py-2 text-sm text-muted-foreground hover:border-primary hover:text-primary"
              data-testid="walter-fact-add-btn"
            >
              <Plus className="h-4 w-4" /> Eintrag hinzufügen
            </button>
          )}
        </div>
      )}
    </div>
  );
}
